/**
 * AdminVideoPlayer Component
 * Simple video player that fetches presigned URLs and plays videos
 */

import { useState, useEffect, useRef } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  RefreshCw,
  AlertCircle,
  Download,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getPlaybackUrl, isUrlExpired } from "../api/video-api";

interface AdminVideoPlayerProps {
  r2Key?: string;
  bucket?: "videos" | "renders";
  directUrl?: string;
  className?: string;
  title?: string;
  onError?: (error: string) => void;
}

export function AdminVideoPlayer({
  r2Key,
  bucket = "renders",
  directUrl,
  className,
  title,
  onError,
}: AdminVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [url, setUrl] = useState<string | null>(directUrl || null);
  const [expiresAt, setExpiresAt] = useState<number>(0);
  const [loading, setLoading] = useState(!directUrl);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  const fetchUrl = async () => {
    if (!r2Key) {
      setError("No video key provided");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await getPlaybackUrl(r2Key, bucket);
      setUrl(result.url);
      setExpiresAt(result.expiresAt);
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : "Failed to load video";
      setError(errorMsg);
      onError?.(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!directUrl && r2Key) {
      fetchUrl();
    }
  }, [r2Key, bucket, directUrl]);

  // Check for URL expiration and refresh if needed
  useEffect(() => {
    if (!r2Key || directUrl || !expiresAt) return;

    const checkExpiration = setInterval(() => {
      if (isUrlExpired(expiresAt)) {
        fetchUrl();
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(checkExpiration);
  }, [r2Key, expiresAt, directUrl]);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };

  const handleMuteToggle = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        videoRef.current.requestFullscreen();
      }
    }
  };

  const handleDownload = () => {
    if (url) {
      const link = document.createElement("a");
      link.href = url;
      link.download = title || "video.mp4";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (loading) {
    return (
      <div className={cn("relative aspect-video", className)}>
        <Skeleton className="absolute inset-0 rounded-lg" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn("relative aspect-video", className)}>
        <Alert variant="destructive" className="h-full flex flex-col justify-center">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="mt-2">
            {error}
            {error.includes("Access denied") && (
              <p className="text-xs mt-1 opacity-75">
                Admin video access requires additional permissions
              </p>
            )}
          </AlertDescription>
          {r2Key && (
            <Button
              variant="outline"
              size="sm"
              onClick={fetchUrl}
              className="mt-3 mx-auto"
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Retry
            </Button>
          )}
        </Alert>
      </div>
    );
  }

  if (!url) {
    return (
      <div className={cn("relative aspect-video bg-muted rounded-lg flex items-center justify-center", className)}>
        <p className="text-sm text-muted-foreground">No video available</p>
      </div>
    );
  }

  return (
    <div className={cn("relative group", className)}>
      {title && (
        <p className="text-sm font-medium mb-2 text-muted-foreground">{title}</p>
      )}
      <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
        <video
          ref={videoRef}
          src={url}
          className="w-full h-full object-contain"
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
          playsInline
        />

        {/* Controls overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="absolute bottom-0 left-0 right-0 p-3 flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-white hover:bg-white/20"
              onClick={handlePlayPause}
            >
              {isPlaying ? (
                <Pause className="h-4 w-4" />
              ) : (
                <Play className="h-4 w-4" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-white hover:bg-white/20"
              onClick={handleMuteToggle}
            >
              {isMuted ? (
                <VolumeX className="h-4 w-4" />
              ) : (
                <Volume2 className="h-4 w-4" />
              )}
            </Button>
            <div className="flex-1" />
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-white hover:bg-white/20"
              onClick={handleDownload}
            >
              <Download className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-white hover:bg-white/20"
              onClick={handleFullscreen}
            >
              <Maximize className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Thumbnail preview component for video list items
 */
export function VideoThumbnail({
  thumbnailUrl,
  className,
}: {
  thumbnailUrl?: string;
  className?: string;
}) {
  if (!thumbnailUrl) {
    return (
      <div
        className={cn(
          "aspect-video bg-muted rounded flex items-center justify-center",
          className,
        )}
      >
        <Play className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className={cn("aspect-video rounded overflow-hidden", className)}>
      <img
        src={thumbnailUrl}
        alt="Video thumbnail"
        className="w-full h-full object-cover"
      />
    </div>
  );
}
